import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { TouchableOpacity, ActivityIndicator, View, ViewStyle } from 'react-native'

import { Leaderboard } from '@trivie/ui-mobile/components/leaderboard'
import { color, Text } from '@trivie/ui-mobile'
import { CountdownTimer } from '@trivie/ui-mobile/components/countdown-timer'
import { roundNumber, useInterval, useStores } from '@trivie/core'

import { ReactComponent as Timeout } from './contest-timeout.svg'
import { DateTime } from 'luxon'
import { useHistory } from 'react-router-dom'

export const ContestTab = observer((props: any) => {
  const { userStore, contestStore } = useStores()
  const { getContestLeaderboard } = contestStore

  const { user } = userStore
  const { showTimeout, lastUpdated, leaderboard, currentPoints, userRank } = contestStore

  const { goToInfo, contest } = props

  const [loading, setLoading] = useState(true)

  const renderLastUpdated = () => {
    const now = DateTime.now()
    const previous = DateTime.fromISO(lastUpdated)
    const diff = now.diff(previous, 'minutes').toObject()
    const { minutes } = diff
    setLastUpdate(roundNumber(minutes ?? 0))
  }

  const renderNextUpdate = () => {
    const now = DateTime.now()
    const updated = DateTime.fromISO(lastUpdated)

    const diff = now.diff(updated, 'minutes').toObject()
    const { minutes } = diff

    if (minutes || minutes === 0) {
      const d = roundNumber(30 - minutes)
      setNextUpdate(d)
    }
  }

  const [lastUpdate, setLastUpdate] = useState<any>()
  const [nextUpdate, setNextUpdate] = useState<any>()

  useInterval(() => {
    renderLastUpdated()
    renderNextUpdate()
  }, 1000)

  useEffect(() => {
    if (nextUpdate === 0) {
      loadLeaderboard(contest?.id)
    }
  }, [nextUpdate])

  let poll
  useEffect(() => {
    return () => {
      clearTimeout(poll)
      contestStore.setShowTimeout(false)
      setLoading(true)
    }
  }, [])

  const history = useHistory()

  const loadLeaderboard = (id) => {
    setLoading(true)
    getContestLeaderboard(id).then((data) => {
      renderLastUpdated()
      renderNextUpdate()
      setLoading(false)
      clearTimeout(poll)
      if (contest) {
        const now = DateTime.now()
        const previous = DateTime.fromISO(contest.end_datetime)
        const diff = now.diff(previous, 'minutes').toObject()
        const { minutes } = diff
        if (!minutes || minutes < 0) {
          return
        } else {
          if (data.kind === 'wait') {
            poll = setTimeout(() => {
              loadLeaderboard(id)
            }, 30000)
          } else if (data.kind === 'not-found') {
            history.goBack()
          }
        }
      }
    })
  }

  useEffect(() => {
    setLoading(true)
    loadLeaderboard(contest.id)
  }, [])

  return loading && !contestStore?.showTimeout ? (
    <View style={LOADER}>
      <ActivityIndicator />
    </View>
  ) : (
    <>
      <View style={{ paddingTop: 20, marginBottom: 12 }}>
        <Text
          variant="body2Emphasized"
          style={{ color: color.shade50 }}
          text={contest?.description}
        />
      </View>
      <View style={{ ...METADATA }}>
        <CountdownTimer
          locale={user?.language ?? 'en'}
          contest
          hideBorder
          date={contest?.end_datetime}
        />
        <TouchableOpacity
          // @ts-ignore
          style={{ cursor: 'pointer' }}
          onPress={goToInfo}
        >
          <Text
            tx="Contests.howToWin"
            variant="body1Medium"
            style={{ color: color.blue, textDecorationLine: 'underline' }}
          />
        </TouchableOpacity>
      </View>
      {lastUpdated && (
        <View
          style={{
            marginTop: 32,
            marginBottom: 12,
            flexDirection: 'row',
            justifyContent: showTimeout ? 'center' : 'space-between',
          }}
        >
          {showTimeout ? (
            <View style={{ flexDirection: 'row', alignSelf: 'center', alignItems: 'center' }}>
              <ActivityIndicator style={{ marginRight: 8 }} />
              <Text
                variant="body2Emphasized"
                style={{ color: color.shade50, fontStyle: 'italic' }}
                text="Updating..."
              />
            </View>
          ) : nextUpdate >= 0 ? (
            <>
              <Text
                variant="body2Emphasized"
                style={{ color: color.shade50, fontStyle: 'italic' }}
                text={`Contest Last Updated: ${`${lastUpdate} min ago`}`}
              />
              <Text
                variant="body2Emphasized"
                style={{ color: color.shade50, fontStyle: 'italic' }}
                text={`Next Update: ${nextUpdate} min`}
              />
            </>
          ) : null}
        </View>
      )}
      {showTimeout ? (
        <div style={NULL_STATE}>
          <Timeout />
          <div style={{ flexDirection: 'column', marginTop: 22 }}>
            <Text
              style={{ marginBottom: 24, textAlign: 'center' }}
              variant="body1Medium"
              text="The contest leaderboard is furiously calculating your updated placement."
            />
            <Text
              style={{ textAlign: 'center' }}
              variant="body1Medium"
              text="Please check back soon!"
            />
          </div>
        </div>
      ) : (
        <Leaderboard
          style={{
            paddingHorizontal: 0,
            paddingLeft: 20,
            paddingTop: 14,
          }}
          contentContainerStyle={{ paddingTop: 24 }}
          contest
          data={leaderboard}
          userPoints={currentPoints}
          userRank={userRank}
        />
      )}
    </>
  )
})

const METADATA: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
}
const LOADER: ViewStyle = {
  flex: 1,
  flexDirection: 'column',
  justifyContent: 'center',
  height: window.innerHeight - 200,
  paddingLeft: 40,
}
const NULL_STATE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: 36,
}
